// ============================================================================
// VISITOR BOOKINGS CARD — booking + visit history for a visitor contact
// Rendered inside StakeholderOverviewTab when contact_type === 'visitor'.
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/visitor-management/people-and-residents.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/visitor-management/people-and-residents)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Clock, KeyRound, RefreshCw, MapPin, User } from 'lucide-react';
import { authService } from '@/lib/authService';

interface VisitorBooking {
  id: number;
  pin: string | null;
  visitor_name: string | null;
  effective_date: string | null;
  visit_time: string | null;
  purpose: string | null;
  status: string | null;
  approval_status: string | null;
  source: string | null;
  host_name?: string | null;
  expires_at: string | null;
  created_at: string | null;
}

interface VisitorBookingsCardProps {
  contactId: number;
  isDark?: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  invited: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  used: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  expired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const APPROVAL_STYLES: Record<string, string> = {
  auto_approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  pending_host: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  denied: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const APPROVAL_LABELS: Record<string, string> = {
  auto_approved: 'Auto-approved',
  approved: 'Approved',
  pending_host: 'Pending host',
  denied: 'Denied',
};

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

export default function VisitorBookingsCard({ contactId, isDark }: VisitorBookingsCardProps) {
  const [bookings, setBookings] = useState<VisitorBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(
        `/api/v1/visitors/bookings?visitor_contact_id=${contactId}&limit=50`,
        { headers: { Authorization: `Bearer ${token || ''}` } },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Failed to load bookings');
      }
      setBookings(json.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    void load();
  }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter(
    (b) =>
      b.effective_date &&
      b.effective_date >= today &&
      !['cancelled', 'expired', 'completed'].includes(b.status || ''),
  );
  const past = bookings.filter((b) => !upcoming.includes(b));

  const renderRow = (booking: VisitorBooking) => (
    <div
      key={booking.id}
      className={`flex items-center gap-3 px-4 py-2.5 ${isDark ? 'border-gray-700' : 'border-gray-100'} border-t`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            {formatDate(booking.effective_date)}
          </span>
          {formatTime(booking.visit_time) && (
            <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <Clock className="h-3 w-3" />
              {formatTime(booking.visit_time)}
            </span>
          )}
          {booking.status && (
            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_STYLES[booking.status] || STATUS_STYLES.expired}`}>
              {booking.status}
            </span>
          )}
          {booking.approval_status && (
            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${APPROVAL_STYLES[booking.approval_status] || ''}`}>
              {APPROVAL_LABELS[booking.approval_status] || booking.approval_status}
            </span>
          )}
        </div>
        <div className={`mt-0.5 flex items-center gap-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {booking.host_name && (
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {booking.host_name}
            </span>
          )}
          {booking.purpose && <span className="truncate">{booking.purpose}</span>}
          {booking.source && <span className="italic">via {booking.source.replace('_', ' ')}</span>}
        </div>
      </div>
      {booking.pin && booking.status === 'active' && (
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-sm font-semibold tracking-widest ${isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-50 text-teal-700'}`}
          title="Access PIN"
        >
          <KeyRound className="h-3.5 w-3.5" />
          {booking.pin}
        </span>
      )}
    </div>
  );

  return (
    <div
      className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}
      data-tour-id="visitor-bookings-card"
    >
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
        <h3 className={`flex items-center gap-2 text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <Calendar className="h-4 w-4 text-teal-500" />
          Visit Bookings
        </h3>
        <button
          type="button"
          onClick={() => void load()}
          className={`rounded-md p-1.5 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
        </button>
      </div>

      {loading ? (
        <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Loading bookings…
        </div>
      ) : error ? (
        <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
          {error}
        </div>
      ) : bookings.length === 0 ? (
        <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <MapPin className="mx-auto mb-2 h-5 w-5 opacity-50" />
          No visit bookings yet
        </div>
      ) : (
        <div>
          {upcoming.length > 0 && (
            <>
              <div className={`px-4 pt-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Upcoming
              </div>
              {upcoming.map(renderRow)}
            </>
          )}
          {past.length > 0 && (
            <>
              <div className={`px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                History
              </div>
              {past.slice(0, 10).map(renderRow)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
